import { useState, useEffect } from "react";
import { loadGoogleMaps } from "../utils/loadGoogleMaps";
import { isGoogleMapsReady } from "../utils/googleMapsReady";

interface UseGoogleMapsLoader {
  isLoaded: boolean;
  error: Error | null;
}

export const useGoogleMapsLoader = (): UseGoogleMapsLoader => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (isGoogleMapsReady()) {
      setIsLoaded(true);
      return;
    }

    let cancelled = false;

    const handleMapsLoaded = () => {
      if (!cancelled && isGoogleMapsReady()) {
        setIsLoaded(true);
        setError(null);
      }
    };

    window.addEventListener("google-maps-loaded", handleMapsLoaded);

    loadGoogleMaps()
      .then(() => {
        if (!cancelled) handleMapsLoaded();
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err);
      });

    const timeoutId = window.setTimeout(() => {
      if (!cancelled && !isGoogleMapsReady()) {
        setError(
          new Error(
            "Google Maps failed to load. Check your network connection and API key.",
          ),
        );
      }
    }, 30_000);

    return () => {
      cancelled = true;
      window.removeEventListener("google-maps-loaded", handleMapsLoaded);
      window.clearTimeout(timeoutId);
    };
  }, []);

  return { isLoaded, error };
};
